import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const SignatureCaptureDialog = ({
  open,
  onOpenChange,
  onSave,
  saving = false,
  defaultCustomerName = ''
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const isDrawingRef = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [customerName, setCustomerName] = useState(defaultCustomerName || '');

  useEffect(() => {
    if (open) {
      setCustomerName(defaultCustomerName || '');
      setHasSignature(false);
      requestAnimationFrame(() => {
        setupCanvas();
      });
    }
  }, [open, defaultCustomerName]);

  useEffect(() => {
    const onResize = () => {
      if (open) {
        setupCanvas();
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  const setupCanvas = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = container.clientWidth;
    const height = 240;

    const prev = canvas.toDataURL('image/png');

    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (hasSignature) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, width, height);
      };
      img.src = prev;
    }
  };

  const getPointerPos = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  };

  const handlePointerDown = (event) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const { x, y } = getPointerPos(event);
    isDrawingRef.current = true;
    ctx.beginPath();
    ctx.moveTo(x, y);
    canvas.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };

  const handlePointerMove = (event) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const { x, y } = getPointerPos(event);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasSignature) setHasSignature(true);
    event.preventDefault();
  };

  const handlePointerUp = () => {
    isDrawingRef.current = false;
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    setHasSignature(false);
  };

  const exportBlob = () => new Promise((resolve) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      resolve(null);
      return;
    }
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/png');
  });

  const handleSave = async () => {
    if (!hasSignature) return;
    const blob = await exportBlob();
    if (!blob) return;
    onSave({ signatureBlob: blob, customerName: customerName.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ maxWidth: '560px', padding: '16px' }}>
        <DialogHeader>
          <DialogTitle>Customer Signature</DialogTitle>
        </DialogHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '14px' }}>
              Customer Name (optional)
            </label>
            <input
              type="text"
              className="form-input"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Enter customer name"
              style={{ width: '100%' }}
            />
          </div>

          <div
            ref={containerRef}
            style={{
              width: '100%',
              border: '1px solid #D1D5DB',
              borderRadius: '10px',
              background: '#FFFFFF',
              overflow: 'hidden'
            }}
          >
            <canvas
              ref={canvasRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onPointerLeave={handlePointerUp}
              style={{ width: '100%', height: '240px', touchAction: 'none', display: 'block' }}
            />
          </div>

          {!hasSignature && (
            <div style={{ fontSize: '12px', color: '#6B7280' }}>
              Please sign in the box using finger or mouse.
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClear}
              disabled={saving}
              style={{ minHeight: '44px', flex: 1 }}
            >
              Clear Signature
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!hasSignature || saving}
              style={{ minHeight: '44px', flex: 1, background: '#FDB515', color: '#000' }}
            >
              {saving ? 'Saving...' : 'Save & Continue'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignatureCaptureDialog;
